import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { RefreshCw, Images, ShieldCheck, Clock, XCircle, Database } from "lucide-react";
import { MemoryBookMediaTile } from "@/components/MemoryBookMediaTile";
import { showError } from "@/lib/errors";

/**
 * V11.3 — Onglet "Images" dédié pour l'éditeur MemoryBook.
 *
 * Affiche la grille complète des médias avec filtres par statut de droits
 * et compteur "à traiter". Aide l'admin à passer en revue rapidement les
 * candidats issus de la découverte automatique.
 */
const FILTERS = [
  { key: "to_verify", label: "À traiter", statuses: ["TO_VERIFY", "UNKNOWN"], icon: Clock, tone: "warn" },
  { key: "validated", label: "Validées", statuses: ["AUTHORIZED", "LICENSED", "INTERNAL"], icon: ShieldCheck, tone: "ok" },
  { key: "rejected", label: "Refusées", statuses: ["REJECTED"], icon: XCircle, tone: "err" },
  { key: "all", label: "Toutes", statuses: null, icon: Images, tone: "neutral" },
];

export function MemoryBookImagesPanel({ requestId, editable = true, onCorpusChanged }) {
  const [medias, setMedias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("to_verify");

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/memory-books/requests/${requestId}/medias`);
      const items = data.items || [];
      setMedias(items);
      onCorpusChanged?.(items);
    } catch (e) {
      showError(e, { prefix: "Chargement images :" });
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [requestId]);

  const counts = useMemo(() => {
    const out = {};
    for (const f of FILTERS) {
      if (f.statuses == null) { out[f.key] = medias.length; continue; }
      out[f.key] = medias.filter((m) => f.statuses.includes(m.rights_status)).length;
    }
    return out;
  }, [medias]);

  const activeFilter = FILTERS.find((f) => f.key === filter);
  const filteredMedias = useMemo(() => {
    if (!activeFilter || activeFilter.statuses == null) return medias;
    return medias.filter((m) => activeFilter.statuses.includes(m.rights_status));
  }, [medias, activeFilter]);

  return (
    <div className="space-y-4" data-testid="mb-images-panel">
      {/* Header avec compteur à traiter */}
      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-3 justify-between">
          <div>
            <h3 className="font-display font-semibold flex items-center gap-2">
              <Images className="h-5 w-5 text-[hsl(var(--aito-gold))]" />
              Images du livret
              {counts.to_verify > 0 && (
                <Badge
                  className="bg-amber-100 text-amber-800 hover:bg-amber-100"
                  data-testid="mb-images-to-treat-badge"
                >
                  {counts.to_verify} à traiter
                </Badge>
              )}
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Valide chaque image (crédit + licence obligatoires) avant qu'elle n'apparaisse dans le PDF.
              Rien n'est publié tant qu'elle n'est pas figée.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={refresh} data-testid="mb-images-refresh">
            <RefreshCw className="h-3.5 w-3.5 mr-1" /> Actualiser
          </Button>
        </div>
      </Card>

      {/* Filtres */}
      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList data-testid="mb-images-filters">
          {FILTERS.map((f) => (
            <TabsTrigger key={f.key} value={f.key} data-testid={`mb-images-filter-${f.key}`}>
              <f.icon className="h-3.5 w-3.5 mr-1.5" />
              {f.label} ({counts[f.key] || 0})
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value={filter} className="mt-4">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-56" />)}
            </div>
          ) : filteredMedias.length === 0 ? (
            <Card className="p-8 text-center" data-testid="mb-images-empty">
              <Database className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">
                {filter === "to_verify"
                  ? "Aucune image en attente de validation. Utilise « Découvrir des images » dans l'onglet Médiathèque pour en proposer."
                  : filter === "validated"
                  ? "Aucune image validée pour l'instant."
                  : filter === "rejected"
                  ? "Aucune image refusée."
                  : "Aucune image dans ce livret. Utilise « Découvrir des images » dans l'onglet Médiathèque."}
              </p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {filteredMedias.map((m) => (
                <MemoryBookMediaTile
                  key={m.id}
                  media={m}
                  disabled={!editable}
                  onChange={refresh}
                  onDelete={async (media) => {
                    if (!window.confirm("Supprimer ce média définitivement ?")) return;
                    try {
                      await api.delete(`/admin/memory-books/medias/${media.id}`);
                      toast.success("Média supprimé.");
                      refresh();
                    } catch (e) {
                      showError(e, { prefix: "Suppression :" });
                    }
                  }}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
